import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import FormatBar from '../format_bar/formatBar';
import './adminTerapi.css';

const AdminTerapi = () => {
  const navigate = useNavigate();
  const textBeforeRef = useRef(null);
  const textAfterRef = useRef(null);
  const fullDescriptionRef = useRef(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Data states
  const [terapiData, setTerapiData] = useState({
    title: '',
    heroImage: '',
    textBefore: '',
    textAfter: ''
  });
  
  const [terapiOmrader, setTerapiOmrader] = useState([]);
  const [editingOmrade, setEditingOmrade] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [heroFile, setHeroFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // UI states
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Format states
  const [textBeforeFormats, setTextBeforeFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  const [textAfterFormats, setTextAfterFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  const [fullDescriptionFormats, setFullDescriptionFormats] = useState({
    bold: false,
    italic: false,
    heading: false,
    bullet: false,
    link: false,
    underline: false
  });

  // Check auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setIsLoading(false);
      if (!user) {
        navigate('/admin/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Load content
  useEffect(() => {
    const fetchTerapiData = async () => {
      try {
        // Fetch terapi main content
        const terapiDocRef = doc(db, 'content', 'terapi');
        const terapiDoc = await getDoc(terapiDocRef);
        
        if (terapiDoc.exists()) {
          const data = terapiDoc.data();
          setTerapiData({
            title: data.title || '',
            heroImage: data.heroImage || '',
            textBefore: data.textBefore || '',
            textAfter: data.textAfter || ''
          });
          
          if (data.heroImage) {
            setHeroPreview(data.heroImage);
          }
        }
        
        // Fetch terapi områder
        const terapiOmraderDocRef = doc(db, 'content', 'terapiOmrader');
        const terapiOmraderDoc = await getDoc(terapiOmraderDocRef);
        
        if (terapiOmraderDoc.exists() && terapiOmraderDoc.data().omrader) {
          setTerapiOmrader(terapiOmraderDoc.data().omrader);
        } else {
          // Fallback til standard terapiområder hvis data ikke findes
          setTerapiOmrader([
            {
              id: 'boern',
              title: 'Børn',
              description: 'Mentaliseringsbaseret terapi (MBT) er en tilgang, der fokuserer på at hjælpe børn og deres omsorgspersoner med at forstå egne og andres tanker, følelser og intentioner.',
              imageUrl: '/media/terapi/boern.avif',
              link: '/terapi/boern'
            },
            {
              id: 'unge',
              title: 'Unge',
              description: 'Når du har overvejet at starte hos en psykolog, så er det formegentligt fordi der er noget som er svært, overvældende og bekymrende for dig.',
              imageUrl: '/media/terapi/unge.avif',
              link: '/terapi/unge'
            },
            {
              id: 'familier',
              title: 'Familier',
              description: 'Det er helt almindeligt, at familier kan opleve udfordringer i deres relationer – uanset om det handler om konflikter, misforståelser eller følelsesmæssige afstande.',
              imageUrl: '/media/terapi/familier.avif',
              link: '/terapi/familier'
            },
            {
              id: 'foraeldre',
              title: 'Forældre',
              description: 'At være forælder er en af livets mest givende roller, men det kan også være en af de mest udfordrende.',
              imageUrl: '/media/terapi/forældre.avif',
              link: '/terapi/foraeldre'
            },
            {
              id: 'voksne',
              title: 'Voksne',
              description: 'At starte i terapi er en beslutning, der ofte kræver mod. Det kan føles som at træde ind i ukendt territorium.',
              imageUrl: '/media/terapi/voksne.avif',
              link: '/terapi/voksne'
            },
            {
              id: 'gruppe-vold',
              title: 'Gruppe for mødre der har levet med vold',
              description: 'TRYGT "FYRTÅRN" I DIT BARNS LIV - Et gruppeforløb til dig der har oplevet at vold i familien har sat alvorlige spor i dit og dit barns liv.',
              imageUrl: '/media/terapi/gruppe.avif',
              link: '/terapi/gruppe-vold'
            }
          ]);
        }
      } catch (error) {
        console.error('Fejl ved hentning af terapi data:', error);
        setMessage({
          text: 'Der opstod en fejl ved indlæsning af indhold',
          type: 'error'
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchTerapiData();
    }
  }, [user]);

  // Fjern MutationObservers - vi læser kun indholdet når vi skal gemme det
  useEffect(() => {
    // Initialisering af editorer
    if (isEditing) {
      if (textBeforeRef.current) {
        textBeforeRef.current.innerHTML = terapiData.textBefore || '';
      }
      if (textAfterRef.current) {
        textAfterRef.current.innerHTML = terapiData.textAfter || '';
      }
    }

    if (editingOmrade) {
      if (fullDescriptionRef.current) {
        fullDescriptionRef.current.innerHTML = editingOmrade.fullDescription || '';
      }
    }
  }, [isEditing, editingOmrade, terapiData.textBefore, terapiData.textAfter]);
  
  const handleSaveOmrade = async () => {
    setIsSaving(true);
    try {
      if (!editingOmrade.id && isCreating) {
        // Generer et ID baseret på titlen hvis det er et nyt område
        editingOmrade.id = editingOmrade.title
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
      }
      
      // Link baseret på ID
      if (!editingOmrade.link) {
        editingOmrade.link = `/terapi/${editingOmrade.id}`;
      }
      
      let imageUrl = editingOmrade.imageUrl;
      
      // Upload nyt billede hvis det er valgt
      if (selectedFile) {
        const storageRef = ref(storage, `terapi-omrader/${editingOmrade.id}-${Date.now()}`);
        await uploadBytes(storageRef, selectedFile);
        imageUrl = await getDownloadURL(storageRef);
      }
      
      // Hent det formaterede indhold fra editoren direkte
      const fullDescription = fullDescriptionRef.current ? fullDescriptionRef.current.innerHTML : '';
      
      const updatedOmrade = {
        ...editingOmrade,
        imageUrl,
        fullDescription
      };
      
      let updatedOmrader;
      
      if (isCreating) {
        updatedOmrader = [...terapiOmrader, updatedOmrade];
      } else {
        updatedOmrader = terapiOmrader.map(o => 
          o.id === updatedOmrade.id ? updatedOmrade : o
        );
      }
      
      // Gem opdateret array i Firestore
      await setDoc(doc(db, 'content', 'terapiOmrader'), {
        omrader: updatedOmrader,
        lastUpdated: new Date()
      });
      
      setTerapiOmrader(updatedOmrader);
      setEditingOmrade(null);
      setSelectedFile(null);
      setPreview(null);
      setIsCreating(false);
      
      setMessage({ 
        type: 'success', 
        text: isCreating ? 'Nyt terapiområde oprettet!' : 'Terapiområde opdateret!' 
      });
    } catch (error) {
      console.error("Fejl ved gem:", error);
      setMessage({ 
        type: 'error', 
        text: 'Fejl ved gem af terapiområde.' 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveHovedindhold = async () => {
    setIsSaving(true);
    try {
      let heroImageUrl = terapiData.heroImage;
      
      // Upload nyt hero billede hvis det er valgt
      if (heroFile) {
        const storageRef = ref(storage, `terapi/hero-${Date.now()}`);
        await uploadBytes(storageRef, heroFile);
        heroImageUrl = await getDownloadURL(storageRef);
      }
      
      // Hent det formaterede indhold fra editorerne direkte
      const textBefore = textBeforeRef.current ? textBeforeRef.current.innerHTML : '';
      const textAfter = textAfterRef.current ? textAfterRef.current.innerHTML : '';
      
      // Gem hovedindhold i Firestore
      await setDoc(doc(db, 'content', 'terapi'), {
        ...terapiData,
        textBefore,
        textAfter,
        heroImage: heroImageUrl,
        lastUpdated: new Date()
      });
      
      // Opdater state med det nye indhold
      setTerapiData(prev => ({
        ...prev,
        textBefore,
        textAfter,
        heroImage: heroImageUrl
      }));
      
      setIsEditing(false);
      setHeroFile(null);
      setMessage({ type: 'success', text: 'Terapisiden blev opdateret!' });
    } catch (error) {
      console.error("Fejl ved gem:", error);
      setMessage({ type: 'error', text: 'Fejl ved gem af terapiside.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTerapiData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleHeroFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setHeroFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handlere for terapiområder
  const handleEditOmrade = (omrade) => {
    setEditingOmrade({...omrade});
    setPreview(omrade.imageUrl);
    setIsCreating(false);
  };

  const handleCreateOmrade = () => {
    setEditingOmrade({
      id: '',
      title: '',
      description: '',
      imageUrl: '',
      link: '',
      fullDescription: ''
    });
    setPreview(null);
    setSelectedFile(null);
    setIsCreating(true);
  };

  const handleOmradeInputChange = (e) => {
    const { name, value } = e.target;
    setEditingOmrade(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteOmrade = async (omradeId) => {
    if (window.confirm('Er du sikker på, at du vil slette dette terapiområde?')) {
      try {
        const omrade = terapiOmrader.find(o => o.id === omradeId);
        
        // Slet billedet hvis det er gemt i storage
        if (omrade.imageUrl && omrade.imageUrl.includes('firebase')) {
          const imagePath = omrade.imageUrl.split('/').pop();
          const imageRef = ref(storage, `terapi-omrader/${imagePath}`);
          await deleteObject(imageRef);
        }
        
        // Opdater array uden det slettede område
        const updatedOmrader = terapiOmrader.filter(o => o.id !== omradeId);
        setTerapiOmrader(updatedOmrader);
        
        // Gem opdateret array i Firestore
        await setDoc(doc(db, 'content', 'terapiOmrader'), {
          omrader: updatedOmrader,
          lastUpdated: new Date()
        });
        
        setMessage({ type: 'success', text: 'Terapiområde blev slettet!' });
      } catch (error) {
        console.error("Fejl ved sletning:", error);
        setMessage({ type: 'error', text: 'Fejl ved sletning af terapiområde.' });
      }
    }
  };

  const handleCancel = () => {
    setEditingOmrade(null);
    setPreview(null);
    setSelectedFile(null);
    setIsCreating(false);
    setIsEditing(false);
    setHeroFile(null);
  };

  const closeMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const goBack = () => {
    navigate('/admin');
  };

  // Tilføj event listener for selection change
  useEffect(() => {
    const handleSelectionChange = () => {
      // Kun tjek formateringer hvis vi er i redigeringstilstand
      if (!isEditing && !editingOmrade) return;
      
      // Find ud af hvilken editor der har fokus
      const activeElement = document.activeElement;
      const isTextBefore = textBeforeRef.current && textBeforeRef.current.contains(activeElement);
      const isTextAfter = textAfterRef.current && textAfterRef.current.contains(activeElement);
      const isFullDescription = fullDescriptionRef.current && fullDescriptionRef.current.contains(activeElement);
      
      if (isTextBefore) {
        checkActiveFormats('textBefore');
      } else if (isTextAfter) {
        checkActiveFormats('textAfter');
      } else if (isFullDescription) {
        checkActiveFormats('fullDescription');
      }
    };

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [isEditing, editingOmrade]);

  const handleEditorFocus = (editorType) => {
    const editor = {
      textBefore: textBeforeRef.current,
      textAfter: textAfterRef.current,
      fullDescription: fullDescriptionRef.current
    }[editorType];

    if (editor) {
      editor.focus();
      checkActiveFormats(editorType);
    }
  };

  const checkActiveFormats = (editorType) => {
    const setFormats = {
      textBefore: setTextBeforeFormats,
      textAfter: setTextAfterFormats,
      fullDescription: setFullDescriptionFormats
    }[editorType];
    
    setFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      heading: false,
      bullet: document.queryCommandState('insertUnorderedList'),
      link: document.queryCommandState('createLink'),
      underline: document.queryCommandState('underline')
    });
  };

  // Håndter link-indsættelse
  const handleLinkInsertion = (editor) => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    
    const range = selection.getRangeAt(0);
    const selectedText = range.toString().trim();
    
    // Tjek om selektionen er inden for editoren
    if (!editor || !editor.contains(range.commonAncestorContainer)) {
      return;
    }
    
    // Tjek om den valgte tekst er en URL
    const url = isValidUrl(selectedText) ? selectedText : prompt('Indtast URL:', 'https://');
    
    if (url && url.trim() !== '') {
      const linkText = selectedText || prompt('Indtast link tekst:', '');
      
      if (linkText && linkText.trim() !== '') {
        // Indsæt link direkte i HTML
        const linkElement = document.createElement('a');
        linkElement.href = url;
        linkElement.target = '_blank';
        linkElement.rel = 'noopener noreferrer';
        linkElement.textContent = linkText;
        
        // Indsæt linket
        range.deleteContents();
        range.insertNode(linkElement);
        
        // Flyt cursoren efter linket
        range.setStartAfter(linkElement);
        range.setEndAfter(linkElement);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleFormatClick = (formatType, editorType) => {
    const editor = {
      textBefore: textBeforeRef.current,
      textAfter: textAfterRef.current,
      fullDescription: fullDescriptionRef.current
    }[editorType];

    if (!editor) return;
    
    // Fokuser på editoren hvis den ikke allerede har fokus
    if (!editor.contains(document.activeElement)) {
      editor.focus();
    }

    // Udfør formateringen direkte
    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false);
        break;
      
      case 'italic':
        document.execCommand('italic', false);
        break;
      
      case 'heading':
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        
        const range = selection.getRangeAt(0);
        if (!range) return;
        
        const parentElement = range.commonAncestorContainer.parentElement;
        const isHeading = parentElement && parentElement.tagName === 'H2';
        
        if (isHeading) {
          document.execCommand('formatBlock', false, 'p');
        } else {
          document.execCommand('formatBlock', false, 'h2');
        }
        break;
      
      case 'bullet':
        document.execCommand('insertUnorderedList', false);
        break;
      
      case 'link':
        handleLinkInsertion(editor);
        break;
      
      case 'underline':
        document.execCommand('underline', false);
        break;
      
      default:
        break;
    }

    // Opdater aktive formateringer
    checkActiveFormats(editorType);
  };

  const isValidUrl = (text) => {
    try {
      if (text.match(/^(https?:\/\/|www\.)/i)) {
        const url = text.startsWith('www.') ? `https://${text}` : text;
        new URL(url);
        return url;
      }
      return false;
    } catch {
      return false;
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      document.execCommand('insertLineBreak', false);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Indlæser...</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <header className="admin-header">
        <div className="admin-logo">
          <img 
            src="/media/logo/psykologiskperspektiv-logo.avif" 
            alt="Psykologisk Perspektiv" 
            onClick={() => navigate('/admin')}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div className="admin-header-right">
          <div className="admin-user">
            <span>Logget ind som: </span>
            <strong>{user.email}</strong>
          </div>
          <button onClick={() => navigate('/admin')} className="back-button">
            ← Tilbage
          </button>
        </div>
      </header>
      
      <main className="admin-content">
        <div className="admin-terapi-container">
          <button className="back-button" onClick={goBack}>
            ← Tilbage til Admin
          </button>
          
          <h1>Administrer Terapi</h1>
          
          {message.text && (
            <div className={`admin-message ${message.type}`}>
              <p>{message.text}</p>
              <button 
                className="close-message" 
                onClick={closeMessage}
              >
                ×
              </button>
            </div>
          )}
          
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Hovedindhold på Terapisiden</h2>
              <button 
                className="edit-content-btn"
                onClick={() => setIsEditing(true)}
                disabled={isEditing}
              >
                Rediger indhold
              </button>
            </div>
            
            {isEditing ? (
              <div className="terapi-form">
                <div className="form-group">
                  <label htmlFor="title">Overskrift</label>
                  <input 
                    type="text" 
                    id="title" 
                    name="title"
                    value={terapiData.title || ''}
                    onChange={handleInputChange}
                  />
                </div>
                
                <div className="image-uploader">
                  <label>Hovedbillede</label>
                  
                  {heroPreview && (
                    <div className="image-preview hero-preview">
                      <img src={heroPreview} alt="Forhåndsvisning" />
                    </div>
                  )}
                  
                  <label className="upload-btn">
                    {heroPreview ? 'Skift billede' : 'Vælg billede'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleHeroFileChange}
                      id="hero-image-upload"
                    />
                  </label>
                </div>
                
                <div className="form-group">
                  <label htmlFor="textBefore">Tekst over terapiområder</label>
                  <FormatBar 
                    activeFormats={textBeforeFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'textBefore')}
                  />
                  <div
                    ref={textBeforeRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('textBefore')}
                    data-placeholder="Skriv teksten der skal vises over terapiområderne..."
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="textAfter">Tekst under terapiområder</label>
                  <FormatBar 
                    activeFormats={textAfterFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'textAfter')}
                  />
                  <div
                    ref={textAfterRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('textAfter')}
                    data-placeholder="Skriv teksten der skal vises under terapiområderne..."
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSaveHovedindhold}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Gemmer...' : 'Gem ændringer'}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            ) : (
              <div className="content-preview">
                <p className="admin-description">
                  Her kan du redigere hovedindholdet på terapisiden, inklusiv overskrift, billede og tekster.
                </p>
                <p>Klik på "Rediger indhold" for at ændre hovedindholdet.</p>
              </div>
            )}
          </div>
          
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Terapiområder</h2>
              {!editingOmrade && (
                <button 
                  className="create-btn"
                  onClick={handleCreateOmrade}
                >
                  + Opret nyt område
                </button>
              )}
            </div>
            
            <p className="admin-description">
              Her kan du administrere de forskellige terapiområder der vises på terapisiden (Børn, Unge, osv.).
            </p>
            
            {editingOmrade ? (
              <div className="terapi-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="omrade-title">Titel</label>
                    <input 
                      type="text" 
                      id="omrade-title" 
                      name="title"
                      value={editingOmrade.title || ''}
                      onChange={handleOmradeInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="omrade-link">Link</label>
                    <input 
                      type="text" 
                      id="omrade-link" 
                      name="link"
                      value={editingOmrade.link || ''}
                      onChange={handleOmradeInputChange}
                    />
                    <span className="form-help-text">
                      Fx. '/terapi/boern' - Lad være tom for at generere automatisk
                    </span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="omrade-description">Kort beskrivelse</label>
                  <textarea 
                    id="omrade-description" 
                    name="description"
                    rows="3"
                    value={editingOmrade.description || ''}
                    onChange={handleOmradeInputChange}
                    required
                  ></textarea>
                  <span className="form-help-text">
                    Kort beskrivelse der vises på kortet på terapisiden
                  </span>
                </div>
                
                <div className="image-uploader">
                  <label>Billede</label>
                  
                  {preview && (
                    <div className="image-preview">
                      <img src={preview} alt="Forhåndsvisning" />
                    </div>
                  )}
                  
                  <label className="upload-btn">
                    {preview ? 'Skift billede' : 'Vælg billede'}
                    <input 
                      type="file" 
                      accept="image/*" 
                      onChange={handleFileChange}
                      id="image-upload"
                    />
                  </label>
                  
                  {!isCreating && !selectedFile && (
                    <span className="form-help-text">
                      Lad være tom for at beholde eksisterende billede
                    </span>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="omrade-fullDescription">Fuld beskrivelse (til undersiden)</label>
                  <FormatBar 
                    activeFormats={fullDescriptionFormats}
                    onFormatClick={(format) => handleFormatClick(format, 'fullDescription')}
                  />
                  <div
                    ref={fullDescriptionRef}
                    className="rich-text-editor"
                    contentEditable
                    onKeyDown={handleKeyDown}
                    onFocus={() => handleEditorFocus('fullDescription')}
                    data-placeholder="Skriv den fulde beskrivelse der vises på terapiområdets egen side..."
                  />
                </div>
                
                <div className="form-actions">
                  <button 
                    className="save-btn"
                    onClick={handleSaveOmrade}
                    disabled={isSaving || !editingOmrade.title || !editingOmrade.description || (isCreating && !preview)}
                  >
                    {isSaving ? 'Gemmer...' : isCreating ? 'Opret område' : 'Gem ændringer'}
                  </button>
                  <button 
                    className="cancel-btn"
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Annuller
                  </button>
                </div>
              </div>
            ) : (
              <>
                {terapiOmrader.length > 0 ? (
                  <div className="terapi-list">
                    {terapiOmrader.map((omrade) => (
                      <div key={omrade.id} className="terapi-item">
                        <div className="terapi-preview">
                          <div className="terapi-image">
                            <img src={omrade.imageUrl} alt={omrade.title} />
                          </div>
                          <div className="terapi-content">
                            <h3>{omrade.title}</h3>
                            <p>{omrade.description}</p>
                            <a className="terapi-link" href={omrade.link} target="_blank" rel="noopener noreferrer">
                              Vis side
                            </a>
                          </div>
                        </div>
                        <div className="terapi-actions">
                          <button 
                            className="edit-btn"
                            onClick={() => handleEditOmrade(omrade)}
                          >
                            Rediger
                          </button>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDeleteOmrade(omrade.id)}
                          >
                            Slet
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-items">
                    <p>Ingen terapiområder fundet. Opret et nyt område for at komme i gang.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      
      <footer className="admin-footer">
        <p>&copy; {new Date().getFullYear()} Psykologisk Perspektiv - Admin Panel</p>
      </footer>
    </div>
  );
};

export default AdminTerapi;
